<template>
  <!-- Proactive AI briefing. Stays hidden until we have something useful to show
       so it never nags an empty/over-quota dashboard. -->
  <div v-if="loading || (briefing && briefing.items.length)" class="card animate-in ai-briefing">
    <div class="ai-briefing-head">
      <div class="flex items-center gap-2">
        <span class="ai-briefing-icon">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </span>
        <div>
          <p class="label-caps" style="margin-bottom: 2px;">{{ $t('briefing.title') }}</p>
          <h3 v-if="briefing" style="color: var(--text-primary); font-weight: 600; font-size: var(--text-base);">{{ briefing.headline }}</h3>
        </div>
      </div>
      <AppButton
        icon-only
        variant="ghost"
        :loading="loading"
        :disabled="loading"
        :aria-label="$t('briefing.refresh')"
        @click="load(true)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </AppButton>
    </div>

    <!-- Skeleton while loading -->
    <div v-if="loading && !briefing" class="ai-briefing-items">
      <AppSkeleton :lines="3" height="14px" width="90%" last-line-width="90%" style="margin: 8px 0;" />
    </div>

    <ul v-else-if="briefing" class="ai-briefing-items">
      <li v-for="(item, i) in briefing.items" :key="i" class="ai-briefing-item">
        <span class="ai-briefing-dot" :style="{ background: dotColor(item.type) }"></span>
        <span class="ai-briefing-text">
          <span style="color: var(--text-secondary); font-size: var(--text-sm); line-height: 1.45;">{{ item.text }}</span>
          <!-- "Here's what happened" becomes "here's what to do about it".
               The action is a key from a server-validated list, resolved to a
               destination here — the model never supplies a route. -->
          <AppButton
            v-if="item.action && ACTION_LABELS[item.action] && !isTabHidden(ACTION_TARGETS[item.action].tab)"
            variant="link"
            class="ai-briefing-action"
            @click="runAction(item.action)"
          >
            {{ $t(ACTION_LABELS[item.action]) }} &rarr;
          </AppButton>
        </span>
      </li>
    </ul>

    <AppButton v-if="!isTabHidden('assistant')" variant="link" class="ai-briefing-cta" @click="$emit('navigate', 'assistant')">
      {{ $t('briefing.ask_assistant') }} &rarr;
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue"
import { type Briefing, type BriefingAction, backendAiService } from "../services/backendAiService"
import { activeHouseholdId } from "../stores/householdStore"
import { canUseAI } from "../stores/subscriptionStore"
import { dispatchAction } from "../stores/uiStore"
import { isTabHidden } from "../utils/focusMode"
import { logger } from "../utils/logger"
import { AppButton, AppSkeleton } from "./ui"

const emit = defineEmits<{ (e: "navigate", tab: string): void }>()

/**
 * Action key → where it goes. The whole mapping lives on the client on
 * purpose: the model chooses from a closed vocabulary and the app decides
 * what each word means, so a new tab layout can never be broken by a cached
 * briefing, and a hallucinated key resolves to nothing rather than a route.
 */
const ACTION_TARGETS: Record<BriefingAction, { tab: string; action?: string }> = {
  review_transactions: { tab: "dashboard", action: "weekly-review" },
  open_budgets: { tab: "budgets" },
  open_transactions: { tab: "transactions" },
  open_insights: { tab: "insights" },
  open_savings: { tab: "budgets" },
  open_assistant: { tab: "assistant" },
}

const ACTION_LABELS: Record<BriefingAction, string> = {
  review_transactions: "briefing.actions.review_transactions",
  open_budgets: "briefing.actions.open_budgets",
  open_transactions: "briefing.actions.open_transactions",
  open_insights: "briefing.actions.open_insights",
  open_savings: "briefing.actions.open_savings",
  open_assistant: "briefing.actions.open_assistant",
}

function runAction(action: BriefingAction) {
  const target = ACTION_TARGETS[action]
  if (!target) return
  emit("navigate", target.tab)
  if (target.action) dispatchAction(target.action)
}

const briefing = ref<Briefing | null>(null)
const loading = ref(false)

function dotColor(type: string): string {
  if (type === "warning") return "var(--amber-600)"
  if (type === "positive") return "var(--forest-600)"
  return "var(--steel-600)"
}

async function load(refresh = false) {
  if (!canUseAI.value || loading.value) return
  loading.value = true
  try {
    briefing.value = await backendAiService.getBriefing(activeHouseholdId.value, refresh)
  } catch (error) {
    // Quota exceeded / not configured / transient — stay quiet on the dashboard.
    logger.warn("AI briefing unavailable", { error: String(error) })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load(false)
})
</script>

<style scoped>
.ai-briefing {
  border-left: 3px solid var(--accent);
}
.ai-briefing-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.ai-briefing-icon {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background: var(--accent-subtle, var(--steel-100));
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ai-briefing-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ai-briefing-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.ai-briefing-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}
.ai-briefing-action {
  font-size: var(--text-xs);
}
.ai-briefing-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 7px;
  flex-shrink: 0;
}
.ai-briefing-cta {
  margin-top: 14px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--accent);
  font-weight: 550;
  font-size: var(--text-sm);
  font-family: var(--font-body);
  padding: 0;
}
</style>
